/**
 * Goal Game — live phone preview for the control page.
 *
 * It renders `GoalFanView`, the SAME component the fan's phone renders, so a
 * preview cannot drift from the real screen. Nothing here re-implements the fan
 * page; it only supplies made-up state (no goal yet / a win) and a brand object
 * assembled from the UNSAVED drafts of the Branding, Design and Wording blocks,
 * laid over whatever is already saved for this card.
 *
 * Nothing is written. Nothing is polled. The only network call is the public
 * brand of this card, read once, as the base underneath the drafts.
 */
import { useEffect, useMemo, useState } from 'react';
import { Smartphone } from 'lucide-react';
import { GoalFanView } from '../GoalFanView';
import { loadFont } from '../../../lib/designTokens';

const SAMPLE_PRIZE = { title_en: 'Basket of wings', title_fr: 'Panier d’ailes',
                       win_en: 'Show this to your server.', win_fr: 'Montrez ceci à votre serveur.' };

const STATES = [
    ['pre', 'Before the goal'],
    ['win', 'Win'],
];

export const GoalFanPreview = ({ cfg, design, brandDraft, copyDraft }) => {
    const [base, setBase] = useState(null);
    const [view, setView] = useState('pre');
    const [lang, setLang] = useState('en');
    const [tonightOpen, setTonightOpen] = useState(false);
    const cardId = cfg?.card_id;

    useEffect(() => {
        if (!cardId) return undefined;
        let alive = true;
        fetch(`${(typeof window !== 'undefined' ? window.location.origin : '')}/api/goal-game/public/${cardId}/brand`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (alive && d) setBase(d); })
            .catch(() => { /* the drafts alone still preview fine */ });
        return () => { alive = false; };
    }, [cardId]);

    const gift = brandDraft?.gift || null;
    const orgColors = brandDraft?.orgColors || null;
    const orgLogo = brandDraft?.orgLogo || null;

    const brand = useMemo(() => {
        const b = gift || {};
        const d = design || null;

        // Colours and type: the Design block's draft wins the moment it is
        // loaded; until then the saved brand shows, so the frame always starts
        // as the fan sees it today.
        // A colour typed in EITHER box counts, whichever was touched last, and
        // it counts straight away: the Branding box and the Design box write the
        // same three keys, and the fan screen reads them the same way.
        const colour = (giftKey, orgKey) => {
            const fromDesign = (d?.[giftKey] ?? '').trim?.() || '';
            const fromBranding = (b?.[giftKey] ?? '').trim?.() || '';
            const own = fromBranding || fromDesign;
            if (!d && !gift) return base?.colors?.[orgKey.replace('voucher_', '')] ?? null;
            const org = orgColors ? (orgColors[orgKey] || '') : (base?.colors?.[orgKey.replace('voucher_', '')] || '');
            return own || org || null;
        };

        return {
            organization_name: cfg?.organization_name || base?.organization_name || '',
            copy: copyDraft || base?.copy || {},
            gift_copy: b.copy || base?.gift_copy || {},
            logo_url: (gift ? (b.merchant_logo_url || orgLogo) : null) || base?.logo_url || null,
            logo_size_px: (gift ? b.logo_size_px : null) || base?.logo_size_px || 48,
            wallpaper_url: (gift ? b.wallpaper_url : null) || base?.wallpaper_url || null,
            wallpaper_zoom: base?.wallpaper_zoom || null,
            scratch_logo_url: (gift ? (b.scratch_surface_logo_url || b.merchant_logo_url) : null)
                || base?.scratch_logo_url || null,
            scratch_logo_opacity: (gift ? b.scratch_surface_logo_opacity : null) ?? base?.scratch_logo_opacity,
            scratch_logo_size: (gift ? b.scratch_surface_logo_size : null) ?? base?.scratch_logo_size,
            colors: {
                bg: colour('gg_voucher_bg', 'voucher_bg'),
                text: colour('gg_voucher_text', 'voucher_text'),
                accent: colour('gg_voucher_accent', 'voucher_accent'),
                button: (b?.gg_voucher_button || d?.gg_voucher_button || base?.colors?.button || null),
            },
            design: d ? {
                headline_font: d.headline_font || '',
                body_font: d.body_font || '',
                headline_size: d.headline_size || '',
                body_size: d.body_size || '',
                headline_scale: d.headline_scale,
                body_scale: d.body_scale,
                button_style: d.gg_button_style || '',
                scores_button: d.gg_scores_button || '',
                standings_button: d.gg_standings_button || '',
                tonight_button: d.gg_tonight_button || '',
                stats_button: d.gg_stats_button || '',
                scorebar_text: d.gg_scorebar_text || '',
                wallpaper_overlay: d.wallpaper_overlay,
                celebration_enabled: d.celebration_enabled !== false,
            } : (base?.design || {}),
        };
    }, [gift, design, orgColors, orgLogo, copyDraft, base, cfg]);

    // The frame downloads the faces it is showing, exactly like a fan's phone.
    useEffect(() => {
        loadFont(brand.design?.headline_font);
        loadFont(brand.design?.body_font);
    }, [brand]);

    const prize = (cfg?.prizes || []).find((p) => p.active) || SAMPLE_PRIZE;
    // The guest's own screens — Choose your winner, Choose your star — need two
    // teams and a few players to show. Sample rows only; nothing is saved and
    // no pick is ever sent from the frame.
    const sampleGuest = {
        winner_pick: { enabled: true, mode: 'game', my_pick: null, locked: false,
                       points: 5000,
                       teams: [{ side: 'home', abbrev: 'HOM', name: 'Home team', rank: '9-3-2 · 20 pts' },
                               { side: 'away', abbrev: 'AWY', name: 'Away team', rank: '4-8-3 · 11 pts' }] },
        star_pick: true,
        live_players: true,
    };
    const samplePlayers = [
        { id: 'p1', name: 'Alex Carter', number: '17', position: 'Centre', team: 'HOM', points: 12 },
        { id: 'p2', name: 'Ben Doyle', number: '9', position: 'Left wing', team: 'HOM', points: 8 },
        { id: 'p3', name: 'Chris Evans', number: '31', position: 'Goal', team: 'AWY', points: 2 },
    ];
    const state = view === 'win'
        ? { ...sampleGuest, table_set_up: true, played: false, event: { id: 'preview', status: 'closed', closed_ago: 1 } }
        : { ...sampleGuest, table_set_up: true, played: false, event: null };
    const outcome = view === 'win' ? { result: 'win', prize } : null;

    return (
        <div className="space-y-3" data-testid="gg-preview">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                <Smartphone size={14} /> Live preview — nothing is saved
            </div>

            <div className="sticky top-[126px] lg:top-[70px] z-20 -mx-1 flex gap-1.5 rounded-lg
                            bg-white/95 px-1 py-2 backdrop-blur dark:bg-slate-900/95"
                 data-testid="gg-preview-states">
                {STATES.map(([key, label]) => (
                    <button key={key} type="button" onClick={() => { setView(key); setTonightOpen(false); }}
                            data-testid={`gg-preview-state-${key}`}
                            className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                                view === key
                                    ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900'
                                    : 'border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300'
                            }`}>
                        {label}
                    </button>
                ))}
            </div>

            <div className="rounded-[2rem] border-[7px] border-slate-900 dark:border-slate-700 bg-slate-900 shadow-xl overflow-hidden w-[300px]"
                 data-testid="gg-preview-frame">
                <div className="overflow-y-auto" data-testid="gg-preview-screen"
                     style={{ height: 'clamp(380px, calc(100vh - 16rem), 600px)' }}>
                    <GoalFanView
                        framed
                        cardId={cardId}
                        brand={brand}
                        state={state}
                        starOffered
                        starPlayers={samplePlayers}
                        lang={lang}
                        onLang={setLang}
                        outcome={outcome}
                        scratched={false}
                        pinToken="preview"
                        tonightOpen={tonightOpen}
                        setupOpen={false}
                        email="" pin="" consent={false}
                        joining={false} error="" lockSeconds={0} checking={false}
                        onTonightOpen={() => setTonightOpen(true)}
                        onTonightBack={() => setTonightOpen(false)}
                        onSetupStart={() => {}}
                        onEmailChange={() => {}}
                        onPinChange={() => {}}
                        onConsentChange={() => {}}
                        onSetupSubmit={(e) => e.preventDefault()}
                        onPinSubmit={(e) => e.preventDefault()}
                        onScratched={() => {}}
                        onCheckAgain={() => {}}
                    />
                </div>
            </div>

            <p className="text-[11px] text-slate-400 w-[300px]">
                The real fan screen, drawn with what is in the Branding, Design and Wording
                boxes right now. Press Save in those boxes to make it real for the tables.
            </p>
        </div>
    );
};

export default GoalFanPreview;
